<script setup>
import { onMounted, computed, ref, toRefs } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { accentForInbox, inboxPhoneDigits, roleForInbox } from 'dashboard/helper/multiNumberHelper';
import { parseGroupSender, buildAgentFirstNames } from './groupSender.js';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';
import BotAvatar from 'next/avatar/BotAvatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { useBranding } from 'shared/composables/useBranding';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
const store = useStore();
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});

// Chat unificado multi-número (Victor 2026-05-23): si la sucursal tiene 2+
// números WhatsApp, cada mensaje se tiñe según el número por el que entró
// (azul = 1er número, verde = 2do). null si la sucursal tiene un solo número.
const numberAccent = computed(() => accentForInbox(store, props.inboxId));
// P2 · 2026-05-30 (Victor): rol del inbox (Principal/Secundario/Makeup) +
// color · reemplaza la etiqueta "entró al …XXXX" por un label inline con
// punto coloreado + nombre del rol + prefijo del número (528-0461).
const inboxRole = computed(() => roleForInbox(store, props.inboxId));
// Últimos 7 dígitos formateados "528-0461" para distinguir cuando los 4 finales
// coinciden (ej. PERSP 528-0461 vs 505-0461).
const numberPrefix = computed(() => {
  if (!inboxRole.value) return null;
  const d = inboxPhoneDigits(inbox.value || {});
  if (!d) return null;
  const last7 = d.slice(-7);
  if (last7.length === 7) return `${last7.slice(0, 3)}-${last7.slice(3)}`;
  return d.slice(-4);
});
// Grupos estilo WhatsApp (Victor 2026-06-05 · P4): los mensajes de grupo traen
// el participante incrustado al inicio del contenido ("👥 *Nombre (id@lid)*").
// Lo parseamos para mostrar avatar + nombre A COLOR (color fijo por persona) +
// etiqueta Cliente/Agente, y limpiar el "(…@lid)". Ver ./groupSender.js.
const agentsGetter = useMapGetter('agents/getAgents');
const agentFirstNames = computed(() => buildAgentFirstNames(agentsGetter.value));
const groupSender = computed(() =>
  props.messageType === MESSAGE_TYPES.INCOMING
    ? parseGroupSender(props.content, agentFirstNames.value)
    : null
);
const groupInitials = computed(() => {
  const n = groupSender.value?.name || '';
  const words = n.replace(/[^\p{L}\s]/gu, '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '#';
  return (words.length === 1 ? words[0][0] : words[0][0] + words[1][0]).toUpperCase();
});
// Contenido a mostrar: en grupos, sin la línea "👥 *Nombre (id)*".
const displayContent = computed(() =>
  groupSender.value ? groupSender.value.cleanContent : props.content
);

const { replaceInstallationName } = useBranding();

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Mensajes "*📱 Enviado desde WhatsApp*" (Victor 2026-05-30):
 * Notas privadas que los agentes crean para registrar respuestas que dieron
 * al cliente desde su celular (WhatsApp móvil). Aunque son private=true,
 * representan mensajes salientes del negocio — deben alinearse a la DERECHA
 * para separarse visualmente de las notas internas reales entre agentes
 * (que siguen a la izquierda con franja dorada).
 */
const isWhatsAppPhoneEcho = computed(() => {
  if (!props.private) return false;
  const c = String(props.content || '');
  return c.startsWith('*📱 Enviado desde WhatsApp*');
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  // Eco desde el celular del agente → DERECHA (representa mensaje saliente).
  if (isWhatsAppPhoneEcho.value) return ORIENTATION.RIGHT;

  // Notas internas a la IZQUIERDA con estilo propio (Victor 2026-05-28):
  // separa visualmente la conversación con el cliente (derecha) de la
  // comunicación entre agentes (izquierda · franja dorada · gris neutro).
  if (props.private) return ORIENTATION.LEFT;

  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  // Grupos: avatar del participante a la IZQUIERDA (estilo WhatsApp).
  if (groupSender.value) return 'grid grid-cols-[24px_1fr]';
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  if (groupSender.value) {
    return `
      "avatar bubble"
      "spacer meta"
    `;
  }
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  // Grupos: SÍ mostramos el avatar del participante a la izquierda (WhatsApp).
  if (groupSender.value) return true;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    // Editar mensaje (Victor 2026-06-06): solo tus mensajes salientes de texto
    // que ya tienen acuse (source_id). El backend valida los ~15 min y que sea
    // bandeja WAHA antes de editar en el WhatsApp del cliente.
    edit:
      isOutgoing &&
      hasText &&
      !!props.sourceId &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

// Avatar especial del bot (Victor 2026-05-28): mensajes salientes enviados por
// AgentBot/Captain → reemplazan el Avatar genérico por BotAvatar (estilo Kommo).
const isBotOutgoingAvatar = computed(() => {
  if (props.messageType !== MESSAGE_TYPES.OUTGOING) return false;
  const senderType = props.sender?.type ?? props.senderType;
  return [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(senderType);
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  // En grupos, las burbujas reciben el contenido SIN la línea "👥 *Nombre (id)*".
  content: displayContent,
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full message-bubble-container"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      shouldGroupWithNext ? 'mb-0.5' : 'mb-2',
      groupSender ? 'msg-accent-group' : (inboxRole ? `msg-accent-${inboxRole.accentClass}` : (numberAccent ? `msg-accent-${numberAccent}` : '')),
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
    :style="groupSender ? { '--grp-color': groupSender.color } : undefined"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="shouldShowAvatar && (groupSender || !shouldGroupWithNext)"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <BotAvatar
          v-if="isBotOutgoingAvatar"
          :size="24"
          :channel-type="inbox.channel_type"
        />
        <!-- Grupos: avatar a color del participante (mismo color que su nombre) -->
        <span
          v-else-if="groupSender"
          class="grp-avatar"
          :style="{ background: groupSender.color }"
          >{{ groupInitials }}</span
        >
        <Avatar v-else v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <div
          class="relative group/msg"
          :class="variant === MESSAGE_VARIANTS.EMAIL ? 'w-full' : 'w-fit max-w-full'"
        >
          <!-- P2 · 2026-05-30 (Victor): identificador de número por ROL.
               Punto coloreado con halo + nombre del rol (Principal/Secundario/
               Makeup) + prefijo del número (528-0461). Reemplaza la etiqueta
               "entró al …0461" que confundía cuando los últimos 4 dígitos
               coincidían entre dos números de la misma sucursal. -->
          <div
            v-if="inboxRole && orientation === ORIENTATION.LEFT && isBubble"
            class="mb-1 inline-flex items-center gap-1.5 text-[10.5px] font-bold leading-none"
          >
            <span
              class="num-role-dot"
              :style="`background:${inboxRole.color};box-shadow:0 0 0 3px ${inboxRole.color}33`"
            ></span>
            <span class="text-white">{{ inboxRole.label }}</span>
            <span class="text-white/25 font-normal">·</span>
            <span class="text-white/40 font-medium" style="font-family:ui-monospace,monospace;font-size:10px">{{ numberPrefix }}</span>
          </div>
          <!-- Grupos estilo WhatsApp (Victor 2026-06-05 · P4): nombre del
               participante A COLOR + etiqueta Cliente/Agente. Sin "(…@lid)". -->
          <div
            v-if="groupSender && isBubble"
            class="mb-1 inline-flex items-center gap-1.5 leading-none"
          >
            <span class="text-[12px] font-bold" :style="{ color: groupSender.color }">{{ groupSender.name }}</span>
            <span
              class="grp-role-tag"
              :class="groupSender.role === 'Agente' ? 'grp-role-agente' : 'grp-role-cliente'"
              >{{ groupSender.role }}</span
            >
          </div>
          <Component :is="componentToRender" />
          <!-- Disparador estilo WhatsApp · ▾ en la esquina sup-der de la burbuja
               (aparece al hover) · abre el menú de mensaje existente -->
          <button
            v-if="isBubble && shouldShowContextMenu && !isMessageDeleted && variant !== MESSAGE_VARIANTS.EMAIL"
            type="button"
            title="Más opciones"
            class="absolute top-0.5 ltr:right-0.5 rtl:left-0.5 z-10 flex items-center justify-center w-5 h-5 rounded-md text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 hover:text-n-slate-12 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150"
            :class="{ '!opacity-100': showContextMenu }"
            @click.stop.prevent="openContextMenu($event)"
          >
            <span class="i-lucide-chevron-down size-4" />
          </button>
        </div>
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}

/* ── W3 · WhatsApp-cristal-negro bubble styling (2026-05-12 PM)
   - Verde sutil #0a3d2c saliente · cristal con borde fino entrante
   - Layout WhatsApp pero con tinte M1.B del CRM
   - Targeting solo bubbles normales (AGENT outgoing + USER incoming)
   - Excluye notas privadas (.bg-n-solid-amber), bots (.bg-n-solid-iris),
     emails y errores
   - 0 cambios a lógica · solo CSS visual */
.message-bubble-container {
  // OUTGOING (agente) · verde oscuro sutil estilo WhatsApp pro
  .right-bubble.bg-n-solid-blue {
    padding: 8px 12px 10px;
    border-radius: 14px;
    border-top-right-radius: 4px;
    background: linear-gradient(180deg, #0a3d2c, #062c20);
    border: 1px solid rgba(34,197,94,0.30);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.5),
      0 2px 12px rgba(34,197,94,0.06),
      inset 0 1px 0 rgba(34,197,94,0.10);
    color: rgba(255,255,255,0.96);
    letter-spacing: -0.05px;
    transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s;

    &:hover {
      transform: translateY(-1px);
      box-shadow:
        0 2px 4px rgba(0,0,0,0.5),
        0 4px 16px rgba(34,197,94,0.12),
        inset 0 1px 0 rgba(34,197,94,0.15);
    }
  }

  // INCOMING (cliente) · cristal negro con borde sutil
  .left-bubble.bg-n-slate-4 {
    padding: 8px 12px 10px;
    border-radius: 14px;
    border-top-left-radius: 4px;
    background: linear-gradient(180deg, #161616, #0e0e0e);
    border: 1px solid rgba(255,255,255,0.06);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.4),
      inset 0 1px 0 rgba(255,255,255,0.03);
    color: rgba(255,255,255,0.92);
    letter-spacing: -0.05px;
    transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.22s;

    &:hover {
      transform: translateY(-1px);
      border-color: rgba(255,255,255,0.10);
    }
  }
}

/* ── Multi-número · color por número (Victor 2026-05-23)
   Chat unificado: cuando la sucursal tiene 2 números, cada burbuja se tiñe
   según el número por el que llegó/salió. Azul = 1er número, verde = 2do.
   - Entrante (cliente): texto del color del número + borde de inicio.
   - Saliente (tu respuesta): borde de cierre del número desde el que respondiste. */
.msg-accent-azul {
  .left-bubble.bg-n-slate-4 {
    color: #7bb6ff;
    border-left: 3px solid #4d9bff;
    border-top-left-radius: 4px;
  }
  .right-bubble.bg-n-solid-blue {
    border-right: 3px solid #4d9bff;
    box-shadow:
      0 1px 2px rgba(0,0,0,0.5),
      0 2px 12px rgba(77,155,255,0.12),
      inset 0 1px 0 rgba(34,197,94,0.10);
  }
}
.msg-accent-verde {
  .left-bubble.bg-n-slate-4 {
    color: #5fe0bd;
    border-left: 3px solid #46d6a6;
    border-top-left-radius: 4px;
  }
  .right-bubble.bg-n-solid-blue {
    border-right: 3px solid #46d6a6;
    box-shadow:
      0 1px 2px rgba(0,0,0,0.5),
      0 2px 12px rgba(70,214,166,0.14),
      inset 0 1px 0 rgba(34,197,94,0.10);
  }
}

/* ── Grupos estilo WhatsApp (Victor 2026-06-05 · P4) ──
   Cada participante tiñe su burbuja con su color fijo (var --grp-color, puesta
   inline en el contenedor). Borde lateral + fondo levemente teñido. */
.msg-accent-group {
  .left-bubble.bg-n-slate-4 {
    border-left: 3px solid var(--grp-color);
    border-top-left-radius: 4px;
    background: color-mix(in srgb, var(--grp-color) 13%, #1c1d22) !important;
  }
}
/* Avatar a color del participante (mismo color que el nombre) */
.grp-avatar {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #14151a;
  flex-shrink: 0;
  user-select: none;
}
/* Etiqueta Cliente / Agente */
.grp-role-tag {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 1px 6px;
  border-radius: 999px;
  line-height: 1.4;
}
.grp-role-agente {
  background: rgba(201, 163, 90, 0.18);
  color: #d4b87a;
}
.grp-role-cliente {
  background: rgba(255, 255, 255, 0.08);
  color: #9fb0b8;
}

/* P2 · 2026-05-30 (Victor) · accents por ROL del inbox.
   Reemplazan los antiguos azul/verde por posición. */
.msg-accent-gold {
  .left-bubble.bg-n-slate-4 {
    border-left: 3px solid #c9a35a;
    border-top-left-radius: 4px;
  }
  .right-bubble.bg-n-solid-blue {
    border-right: 3px solid #c9a35a;
    box-shadow:
      0 1px 2px rgba(0,0,0,0.5),
      0 2px 12px rgba(201,163,90,0.16),
      inset 0 1px 0 rgba(201,163,90,0.12);
  }
}
.msg-accent-blue {
  .left-bubble.bg-n-slate-4 {
    border-left: 3px solid #3b82f6;
    border-top-left-radius: 4px;
  }
  .right-bubble.bg-n-solid-blue {
    border-right: 3px solid #3b82f6;
    box-shadow:
      0 1px 2px rgba(0,0,0,0.5),
      0 2px 12px rgba(59,130,246,0.16),
      inset 0 1px 0 rgba(59,130,246,0.12);
  }
}
.msg-accent-pink {
  .left-bubble.bg-n-slate-4 {
    border-left: 3px solid #ec4899;
    border-top-left-radius: 4px;
  }
  .right-bubble.bg-n-solid-blue {
    border-right: 3px solid #ec4899;
    box-shadow:
      0 1px 2px rgba(0,0,0,0.5),
      0 2px 12px rgba(236,72,153,0.18),
      inset 0 1px 0 rgba(236,72,153,0.14);
  }
}

/* Puntito del label de rol del número (P2) */
.num-role-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
